import { getPreferredPhone, useGetAllContacts, useGetContact } from "@api/contactService";
import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { intervalToDuration } from "date-fns";
import { getBusinessLogo, useGetBusiness } from "@api/businessService";
import { formatUrl } from "@utils/formatUrl";
import { yesNo } from "@utils/yesNo";
import { ContextualHelp } from "@components/ContextualHelp";

/*
interface ShowData {
    label?: string;
    conditional?: Function|boolean;
    content?: Function|string;
};
*/

const CardHeader = () => {
    const data = useContext(ObjectContext);
    const avatarInitials = data.name ? data.name.split(' ').map(i => i.charAt(0)).join('').substr(0, 3).toUpperCase() : '';

    return (
    <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        p={1.5}
        pt={0}
    >
        <Avatar
            size="lg"
            sx={{
                '--Avatar-size': '5rem',
                backgroundColor: '#fff',
                'img': {
                    objectFit: 'contain',
                },
            }}
            src={getBusinessLogo(data)?.url}
        >
            {avatarInitials}
        </Avatar>
        <Box sx={{flex: 1}}>
            <Typography fontSize={'md'} fontWeight={'bold'}>{data.name}</Typography>
            {data.title && <Typography fontSize={'sm'}>{data.title}</Typography>}
            {data.dba_name && data.name !== data.dba_name && <Typography fontSize={'sm'}><i>dba</i> {data.dba_name}</Typography>}
        </Box>
    </Stack>
)
};

const dataToDisplay/*: readonly ShowData[]*/ = [
    {
        label: 'Business Type',
        conditional: object => !!object.business_type_id,
        content: (object) => object.business_type?.name,
    },
    {
        label: 'Primary Contact',
        conditional: object => !!object.primary_contact,
        content: (object) => <ObjectPreview to={`/contacts/${object.primary_contact_id}`} object={object.primary_contact}>{object.primary_contact.name}</ObjectPreview>,
    },
    {
        label: 'Communications',
        // conditional: true,
        content: (object) => object.do_not_contact ? 'Do Not Contact' : 'Okay to Contact',
    },
    {
        label: 'Phone',
        conditional: object => !!object.phone,
        content: (object) => <a href={`tel:${object.phone}`}>{object.phone}</a>,
    },
    {
        label: 'Fax',
        conditional: object => !!object.fax,
        content: 'fax',
    },
    {
        label: 'Email',
        conditional: object => !!object.email,
        content: (object) => <a href={`mailto:${object.email}`}>{object.email}</a>,
    },
    {
        label: 'Mailing Address',
        conditional: object => !!object.address_mail,
        content: (object) => <span dangerouslySetInnerHTML={{__html: generateAddress(object, false, '_mail')}}></span>,
    },
    {
        label: 'Membership',
        conditional: object => !!object.membership_level_id,
        content: 'membership_level_name',
    },
    {
        label: 'Tax ID',
        conditional: object => !!object.tax_id,
        content: 'tax_id',
    },
    // tax rate
    {
        label: 'Website',
        conditional: object => !!object.url,
        content: (object) => <a href={formatUrl(object.url)} target="_blank">{object.url}</a>,
    },
    {
        label: 'Facebook',
        conditional: object => !!object.facebook_url,
        content: (object, label) => <a href={formatUrl(object.facebook_url)} target="_blank">View {label}</a>,
    },
    {
        label: 'Instagram',
        conditional: object => !!object.instagram_url,
        content: (object, label) => <a href={formatUrl(object.instagram_url)} target="_blank">View {label}</a>,
    },
    {
        label: 'X (Twitter)',
        conditional: object => !!object.twitter_url,
        content: (object, label) => <a href={formatUrl(object.twitter_url)} target="_blank">View {label}</a>,
    },
    // google plus
    {
        label: 'LinkedIn',
        conditional: object => !!object.linkedin_url,
        content: (object, label) => <a href={formatUrl(object.linkedin_url)} target="_blank">View {label}</a>,
    },
    {
        label: 'YouTube',
        conditional: object => !!object.youtube_url,
        content: (object, label) => <a href={formatUrl(object.youtube_url)} target="_blank">View {label}</a>,
    },
    {
        label: 'Yelp',
        conditional: object => !!object.yelp_url,
        content: (object, label) => <a href={formatUrl(object.yelp_url)} target="_blank">View {label}</a>,
    },
    {
        label: 'Description',
        conditional: object => !!object.description && object.description.trim().length > 0,
        content: 'description',
    },
    {
        label: 'Not for Profit',
        content: (object) => yesNo(object.not_for_profit),
    },
    {
        label: <ContextualHelp slug="not_in_district">In District</ContextualHelp>,
        content: (object) => yesNo(!object.not_in_district),
    },
    {
        label: 'Opened Date',
        conditional: object => isValidDate(object.open_date),
        content: (object) => formatAbsoluteDate(object.open_date),
    },
    {
        label: 'Closed Date',
        conditional: object => isValidDate(object.close_date),
        content: (object) => formatAbsoluteDate(object.close_date),
    },
    {
        label: <ContextualHelp slug="moved_in_date">Relocated In Date</ContextualHelp>,
        conditional: object => isValidDate(object.moved_in_date),
        content: (object) => formatAbsoluteDate(object.moved_in_date),
    },
    {
        label: <ContextualHelp slug="moved_out_date">Relocated Out Date</ContextualHelp>,
        conditional: object => isValidDate(object.moved_out_date),
        content: (object) => formatAbsoluteDate(object.moved_out_date),
    },
    /*
    {
        label: 'Notes',
        conditional: object => object.notes && object.notes.trim().length,
        content: 'notes',
    },
    */
    {
        label: 'Demographics',
        conditional: object => !!object.is_veteran || !!object.is_minority || !!object.is_female || !!object.is_lgbt,
        content: (object) => (<>
            {!!object.is_veteran && <Typography sx={{display: 'block'}}>Veteran-owned</Typography>}
            {!!object.is_minority && <Typography sx={{display: 'block'}}>Minority-owned</Typography>}
            {!!object.is_female && <Typography sx={{display: 'block'}}>Woman-owned</Typography>}
            {!!object.is_lgbt && <Typography sx={{display: 'block'}}>LGBT-owned</Typography>}
        </>),
    },

];

export default function BusinessShow() {

    return (
        <ObjectShow
            query={useGetBusiness}
            type={'business'}
            permissionType={'businesses'}
            title={'Businesses'}
            subtitleKey={'name'}
            color={'business'}
        >
            <ObjectColumn primary>
                <ObjectPrimaryInfo
                    title={'Business Info'}
                    tint={'business'}
                    fields={dataToDisplay}
                    cardHeader={<CardHeader/>}
                />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationship type={'contacts'} />
                <ObjectRelationship type={'owned_properties'} />
                <ObjectRelationship type={'involvements'} />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationship type={'hours'} />
                <ObjectRelationship type={'notes'} permissions={['edit businesses', 'edit basic businesses']} />
                <ObjectRelationship type={'employees'} permissions={['edit businesses', 'edit basic businesses']} />
                <ObjectRelationship type={'contact_reports'} />
                <ObjectRelationship type={'files'} />
                <ObjectRelationship
                    type={'donation_entries'}
                    permissions={'view donations'}
                />
            </ObjectColumn>

            <ObjectColumn full>
                <ObjectRelationship type={'business_locations'} />
            </ObjectColumn>

        </ObjectShow>
    )
}
